/**
 * Fila offline do Portal Checklist — deduplicação e normalização antes do sync.
 */

function newClientId() {
  return `ck_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/** Snapshot da execução no momento do enqueue (detecção de conflito no sync). */
export function buildExecSnapshot(exec) {
  if (!exec) return null;
  return {
    updatedAt: exec.updatedat || exec.updatedAt || null,
    idUsuario: exec.idusuario ?? exec.idUsuario ?? null,
    status: exec.status || null,
    qtdRespondidos: exec.qtd_respondidos ?? exec.qtdRespondidos ?? null,
  };
}

export function clearAcaoErro(list, clientId) {
  return (list || []).map((a) =>
    a.clientId === clientId ? { ...a, erro: null, aviso: null } : a
  );
}

export function isConflitoErro(acao) {
  return acao?.erro?.code === "CONFLICT";
}

/** Agrupa salvar por execução (última resposta por item) e mantém um único concluir por execução. */
export function normalizeQueue(list) {
  if (!Array.isArray(list) || !list.length) return [];

  const byExec = new Map();

  for (const raw of list) {
    const acao = { ...raw };
    const idExec = acao.payload?.idExecucao;
    if (!idExec) continue;

    if (!byExec.has(idExec)) {
      byExec.set(idExec, {
        salvarRespostas: new Map(),
        indiceAtual: null,
        concluir: null,
        meta: acao,
        execSnapshot: acao.payload?.execSnapshot || null,
      });
    }
    const bucket = byExec.get(idExec);
    if (acao.payload?.execSnapshot) {
      bucket.execSnapshot = acao.payload.execSnapshot;
    }

    if (acao.tipo === "salvar") {
      const respostas = acao.payload?.respostas;
      if (Array.isArray(respostas)) {
        for (const r of respostas) {
          const idItem = r.idItemModelo ?? r.iditemmodelo;
          if (idItem == null) continue;
          bucket.salvarRespostas.set(String(idItem), r);
        }
      }
      if (acao.payload?.indiceAtual != null) {
        bucket.indiceAtual = acao.payload.indiceAtual;
      }
      if (!bucket.meta || (acao.ts || 0) >= (bucket.meta.ts || 0)) {
        bucket.meta = { ...acao, tipo: "salvar" };
      }
    } else if (acao.tipo === "concluir") {
      if (!bucket.concluir || (acao.ts || 0) >= (bucket.concluir.ts || 0)) {
        bucket.concluir = acao;
      }
    }
  }

  const out = [];
  for (const [idExecucao, bucket] of byExec) {
    if (bucket.salvarRespostas.size > 0) {
      const respostas = [...bucket.salvarRespostas.values()];
      const base = bucket.meta || { tipo: "salvar", ts: Date.now() };
      out.push({
        clientId: base.clientId || newClientId(),
        tipo: "salvar",
        ts: base.ts || Date.now(),
        payload: {
          idExecucao: Number(idExecucao),
          indiceAtual: bucket.indiceAtual,
          respostas,
          execSnapshot: bucket.execSnapshot || base.payload?.execSnapshot || null,
        },
        erro: base.erro || null,
        tentativas: base.tentativas || 0,
      });
    }
    if (bucket.concluir) {
      out.push({
        ...bucket.concluir,
        clientId: bucket.concluir.clientId || newClientId(),
        payload: {
          ...bucket.concluir.payload,
          idExecucao: Number(idExecucao),
          execSnapshot:
            bucket.concluir.payload?.execSnapshot
            || bucket.execSnapshot
            || null,
        },
        erro: bucket.concluir.erro || null,
        tentativas: bucket.concluir.tentativas || 0,
      });
    }
  }

  out.sort((a, b) => (a.ts || 0) - (b.ts || 0));
  return out;
}

export function loadQueue(storageKey) {
  try {
    const raw = localStorage.getItem(storageKey);
    const list = raw ? JSON.parse(raw) : [];
    return normalizeQueue(Array.isArray(list) ? list : []);
  } catch {
    return [];
  }
}

export function saveQueue(storageKey, list) {
  const normalized = normalizeQueue(list);
  localStorage.setItem(storageKey, JSON.stringify(normalized));
  return normalized;
}

/** Insere ou atualiza ações na fila (com deduplicação automática). */
export function enqueueAction(storageKey, tipo, payload, prevList) {
  const item = {
    clientId: newClientId(),
    tipo,
    payload,
    ts: Date.now(),
    erro: null,
    tentativas: 0,
  };
  return saveQueue(storageKey, [...(prevList || loadQueue(storageKey)), item]);
}

export function labelAcao(acao) {
  if (!acao) return "—";
  const id = acao.payload?.idExecucao;
  if (acao.tipo === "concluir") return `Concluir execução #${id}`;
  const n = acao.payload?.respostas?.length ?? 0;
  return `Salvar ${n} resposta(s) — execução #${id}`;
}

export function descricaoErroSync(acao) {
  if (!acao?.erro) return null;
  const { code, mensagem, detalhes } = acao.erro;
  if (code === "CONFLICT" && detalhes?.nomeUsuarioServidor) {
    return `Conflito: ${detalhes.nomeUsuarioServidor} alterou esta execução. ${mensagem || ""}`.trim();
  }
  return mensagem || "Erro na sincronização";
}
